/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useState, useMemo, useEffect } from 'react';

/**
 * Generic search hook for filtering entities by multiple searchable fields
 * Supports column-specific search syntax: "column:value"
 *
 * @param items - Full list of items to search
 * @param searchFields - Function that returns array of searchable field values for an item
 * @param fieldMap - Optional map of column names to their indices in searchFields array
 * @param exactMatchFields - Optional set of field names that should use exact matching instead of substring
 * @returns Search query state and filtered items
 *
 * @example
 * // Basic usage (searches all fields)
 * const { searchQuery, setSearchQuery, filteredItems } = useEntitySearch(
 *   clips,
 *   (clip) => [clip.name, clip.tape_name, ...(clip.tags || []), JSON.stringify(clip.custom_metadata)]
 * );
 *
 * @example
 * // Advanced usage with column-specific search and exact matching
 * const { searchQuery, setSearchQuery, filteredItems } = useEntitySearch(
 *   clips,
 *   (clip) => [clip.name, clip.tape_name, clip.scene, ...],
 *   { 'name': 0, 'tape': 1, 'scene': 2 },
 *   new Set(['scene']) // 'scene' will use exact match
 * );
 * // Now supports: "scene:15" (exact), "tape:A001" (partial), or just "15" for all fields
 */
export function useEntitySearch<T>(
  items: T[],
  searchFields: (item: T) => (string | undefined | null)[],
  fieldMap?: Record<string, number>,
  exactMatchFields?: Set<string>
) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query (300ms delay for better performance with large datasets)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredItems = useMemo(() => {
    const trimmed = debouncedQuery.trim();
    if (!trimmed) return items;

    // Check if query has column-specific syntax (e.g., "scene:15")
    const colonIndex = trimmed.indexOf(':');
    const hasColumnSpecifier = colonIndex > 0 && colonIndex < trimmed.length - 1;

    if (hasColumnSpecifier && fieldMap) {
      const columnName = trimmed.substring(0, colonIndex).toLowerCase().trim();
      const searchValue = trimmed.substring(colonIndex + 1).toLowerCase().trim();

      // Check if the column name exists in the field map
      const fieldIndex = fieldMap[columnName];
      if (fieldIndex !== undefined) {
        // Determine if this field should use exact matching
        const useExactMatch = exactMatchFields?.has(columnName) ?? false;

        // Search only in the specified column
        return items.filter(item => {
          const fields = searchFields(item);
          const field = fields[fieldIndex];
          if (field === null || field === undefined) return false;

          const fieldLower = field.toLowerCase();

          // Use exact match or substring match based on configuration
          if (useExactMatch) {
            return fieldLower === searchValue;
          } else {
            return fieldLower.includes(searchValue);
          }
        });
      }
    }

    // Default behavior: search all fields
    const query = trimmed.toLowerCase();

    // Optimize: pre-allocate result array
    const results: T[] = [];

    for (const item of items) {
      const fields = searchFields(item);

      // Early exit: check each field and break on first match
      let matched = false;
      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        if (field !== null && field !== undefined) {
          if (field.toLowerCase().includes(query)) {
            matched = true;
            break; // Stop checking fields for this item
          }
        }
      }

      if (matched) {
        results.push(item);
      }
    }

    return results;
  }, [items, debouncedQuery, searchFields, fieldMap]);

  return {
    searchQuery,
    setSearchQuery,
    filteredItems
  };
}
